import type React from "react";
import { useEffect, useState } from "react";

import type { CompanyDto } from "@/api/types";
import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { CheckboxMultiSelect } from "@/design-system/CheckboxMultiSelect";
import { FormField } from "@/design-system/FormField";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { getUserStatusLabelByType, USER_STATUSES, type UserStatus } from "@/helpers/user-statuses";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useTranslation } from "@/translations";

export type UserFilterValues = {
  statuses?: UserStatus[];
  companyId?: string;
};

type UserOverviewFilterDrawerProps = DrawerBaseProps & {
  filter: UserFilterValues;
  filterOptions: {
    companies: CompanyDto[];
  };
  onUpdate: (filter: UserFilterValues) => void;
};

export function UserOverviewFilterDrawer({
  filter,
  filterOptions,
  onUpdate,
  ...drawerProps
}: UserOverviewFilterDrawerProps): React.ReactNode {
  const [currFilter, setCurrFilter] = useState<UserFilterValues>(filter);
  const { t } = useTranslation();
  const sessionUser = useSessionUser();

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setCurrFilter(filter);
  }, [filter]);

  const handleApplyFilter = () => {
    onUpdate(currFilter);
  };

  const handleClearFilter = () => {
    onUpdate({
      statuses: [],
      companyId: undefined,
    });
  };

  // `deleted` is for Superadmin only
  const userStatusOptions = USER_STATUSES.map((status) => ({
    id: status,
    name: getUserStatusLabelByType(t, status),
  })).filter((status) => status.id !== "deleted" || sessionUser.isSuperAdmin);

  return (
    <Drawer.Root data-testid="filter-drawer" title={t("page.user-overview.filter-drawer.title")} {...drawerProps}>
      <Drawer.Body>
        <Stack gap="md">
          {filterOptions.companies.length > 0 && (
            <FormField label={t("page.user-overview.filter-drawer.companies.label")}>
              <Select
                items={filterOptions.companies}
                selected={filterOptions.companies.find((c) => c.id === currFilter.companyId)}
                onChange={(e) => setCurrFilter({ ...currFilter, companyId: e?.id })}
                keySelector={(x) => x.id}
                renderOption={(x) => x.name}
                emptyItem={t("page.user-overview.filters.companies.all")}
              />
            </FormField>
          )}

          <FormField label={t("page.user-overview.filter-drawer.statuses.label")}>
            <CheckboxMultiSelect
              renderMoreSelected={(count) => t("common.label.x-more", { count })}
              items={userStatusOptions}
              selected={userStatusOptions.filter((s) => currFilter.statuses?.includes(s.id))}
              onChange={(currStatus) => setCurrFilter({ ...currFilter, statuses: currStatus.map(({ id }) => id) })}
              placeholder={t("page.user-overview.filter-drawer.statuses.placeholder")}
              searchableFieldSelector={(x) => [x.name]}
              renderOption={(x) => {
                let variant: "success" | "danger" | "warning" = "warning";
                if (x.id === "onboarded") {
                  variant = "success";
                } else if (x.id === "deleted") {
                  variant = "danger";
                }

                return <Badge variant={variant}>{x.name}</Badge>;
              }}
              keySelector={(x) => x.id}
            />
          </FormField>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClearFilter}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button data-testid="apply-btn" onClick={handleApplyFilter}>
            {t("common.action.apply")}
          </Button>
        </Drawer.Close>
      </Drawer.Actions>
    </Drawer.Root>
  );
}
